import React, { useEffect, useState } from "react";
import {
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  styled,
  MenuItem,
  Box,
  Button,
  Table,
  AppBar,
  Toolbar,
  Typography,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ButtonGroup,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom"; // Import useParams
import InfoIcon from "@mui/icons-material/Info";
import TimePicker from 'react-time-picker';
import 'react-time-picker/dist/TimePicker.css';
import 'react-clock/dist/Clock.css';

const styles = `
  .custom-time-picker {
    width: 100%;
  }
  .custom-time-picker .react-time-picker__wrapper {
    border: 1px solid rgba(0, 0, 0, 0.23);
    border-radius: 4px;
    padding: 16.5px 14px;
    width: 100%;
  }
  .custom-time-picker .react-time-picker__wrapper:hover {
    border-color: rgba(0, 0, 0, 0.87);
  }
`;


const AdmitPage = () => {
  const { encodedFormId } = useParams(); // Retrieve encodedFormId from URL
  const navigate = useNavigate();
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [examTypes, setExamTypes] = useState([]);
  const [subExams, setSubExams] = useState([]);
  const [selectedExamType, setSelectedExamType] = useState("");
  const [selectedSubExam, setSelectedSubExam] = useState("");
  const [classNames, setClassNames] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [selectedSession, setSelectedSession] = useState("");
  const [subjectDetails, setSubjectDetails] = useState([]);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [isExamModeIndependent, setIsExamModeIndependent] = useState(0);
  const [isSubExamDisabled, setIsSubExamDisabled] = useState(false);

  // Add logging to debug encodedFormId
  console.log("AdmitPage - Initial encodedFormId:", encodedFormId);

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 20,
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
    },
  }));

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const handleDateChange = (index, event) => {
    const updatedSubjectDetails = [...subjectDetails];
    updatedSubjectDetails[index].date = event.target.value;
    setSubjectDetails(updatedSubjectDetails);
  };

  const handleStartTimeChange = (index, event) => {
    const updatedSubjectDetails = [...subjectDetails];
    updatedSubjectDetails[index].startTime = event.target.value;
    setSubjectDetails(updatedSubjectDetails);
  };

  const handleEndTimeChange = (index, event) => {
    const updatedSubjectDetails = [...subjectDetails];
    updatedSubjectDetails[index].endTime = event.target.value;
    setSubjectDetails(updatedSubjectDetails);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Navigation handlers
  const handleNavigateToAdmitCard = () => {
    console.log("Navigating to AdmitCard with encodedFormId:", encodedFormId);
    navigate(`/admitcard/${encodedFormId}`);
  };

  const handleNavigateToCreateNewAdmit = () => {
    console.log("Navigating to AdmitPage with encodedFormId:", encodedFormId);
    navigate(`/admitpage/${encodedFormId}`);
  };

  useEffect(() => {
    const fetchExamTypes = async () => {
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/ExaminationCriteria/GetExaminationCriteria`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: 0 }),
        });
        const data = await response.json();
        if (data && Array.isArray(data)) {
          setExamTypes(data);
        } else {
          setSnackbarMessage("No exam types found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching exam types:", error);
        setSnackbarMessage("Failed to fetch exam types. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchExamTypes();
  }, []);

  useEffect(() => {
    if (!selectedExamType) return;
    const selectedExam = examTypes.find(
      (exam) => exam.examCriteriaId === selectedExamType
    );
    if (selectedExam) {
      setIsExamModeIndependent(selectedExam.isExamModeIndependent);
      if (selectedExam.isExamModeIndependent === 1) {
        setIsSubExamDisabled(true);
        setSelectedSubExam("");
        setSubExams([]);
        setSnackbarMessage(
          "Sub-exam selection is disabled as the exam mode is independent."
        );
        setSnackbarSeverity("info");
        setSnackbarOpen(true);
      } else {
        setIsSubExamDisabled(false);
      }
    }
  }, [selectedExamType, examTypes]);

  useEffect(() => {
    if (!selectedExamType || isExamModeIndependent === 1) return;
    const fetchSubExams = async () => {
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/Teacher/ddlExamSubCategory_Examination`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ examCategoryId: selectedExamType }),
        });
        const data = await response.json();
        if (data && Array.isArray(data) && data.length > 0) {
          const mappedSubExams = data.map((subExam) => ({
            id: subExam.examSubCategoryId,
            name: subExam.examSubCategory,
          }));
          setSubExams(mappedSubExams);
        } else {
          setSubExams([]);
          setSnackbarMessage("No sub-exams found for the selected exam type.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching sub exams:", error);
        setSnackbarMessage("Failed to fetch sub-exams. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchSubExams();
  }, [selectedExamType, isExamModeIndependent]);

  useEffect(() => {
    const fetchSessions = async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_BASE_URL}/ClassPromotion/GetFinancialYear`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        });
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          const mappedSessions = data.map((session) => ({
            id: session.financialYearID,
            name: session.finanacialYear,
          }));
          setSessions(mappedSessions);
          const today = new Date();
          const currentYear = today.getFullYear();
          const nextYear = currentYear + 1;
          const financialYearStart = new Date(currentYear, 3, 1);
          const financialYearEnd = new Date(nextYear, 2, 31);
          let currentSessionString =
            today >= financialYearStart && today <= financialYearEnd
              ? `${currentYear}-${nextYear}`
              : `${currentYear - 1}-${currentYear}`;
          const currentSession = mappedSessions.find(
            (session) => session.name === currentSessionString
          );
          if (currentSession) {
            setSession(currentSession.id);
          }
        } else {
          setSnackbarMessage("No sessions found.");
          setSnackbarSeverity("warning");
          setSnackbarOpen(true);
        }
      } catch (error) {
        console.error("Error fetching financial years:", error);
        setSnackbarMessage("Failed to fetch sessions. Please try again.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchSessions();
  }, []);

  useEffect(() => {
    const fetchClassNames = async () => {
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/CreateClass/GetClassName`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({}),
        });
        const data = await response.json();
        const mappedClassNames = data.map((classData) => ({
          id: classData.classId,
          name: classData.className,
        }));
        setClassNames(mappedClassNames);
      } catch (error) {
        console.error("Error fetching class names:", error);
      }
    };
    fetchClassNames();
  }, []);

  useEffect(() => {
    const fetchSections = async () => {
      if (!selectedClass) return;
      try {
        const Url = process.env.REACT_APP_BASE_URL;
        const apiUrl = `${Url}/Exam/ddlSection_clsId`;
        const token = sessionStorage.getItem("token");
        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({ classId: selectedClass }),
        });
        const data = await response.json();
        const mappedSections = data.map((section) => ({
          id: section.sectionId,
          name: section.sectionName,
        }));
        setSections(mappedSections);
      } catch (error) {
        console.error("Error fetching sections:", error);
      }
    };
    fetchSections();
  }, [selectedClass]);

  const handleGetDetails = async () => {
    if (
      !selectedExamType ||
      (!isSubExamDisabled && !selectedSubExam) ||
      !selectedClass ||
      !selectedSection ||
      !session
    ) {
      setOpenDialog(true);
      return;
    }
    try {
      const Url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${Url}/AdmitCard/GetSubjectDetail_ToGenerateAdmitCard`;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify({
          examType: selectedExamType,
          subExamId: isSubExamDisabled ? 0 : selectedSubExam,
          session: session,
          classId: selectedClass,
          sectionId: selectedSection,
        }),
      });
      const data = await response.json();
      const examDetails = examTypes.find(
        (type) => type.examCriteriaId === selectedExamType
      );
      const minMarks = examDetails ? examDetails.minNumber : 0;
      const maxMarks = examDetails ? examDetails.maxNumber : 0;
      const updatedSubjectDetails = data.data.map((subject) => ({
        subjectId: subject.subjectId,
        subjectName: subject.subjectName,
        date: "",
        startTime: "",
        endTime: "",
        minMarks: minMarks,
        maxMarks: maxMarks,
      }));
      setSubjectDetails(updatedSubjectDetails);
      setSnackbarMessage("Generated Admit card data retrieved successfully.");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error fetching subject details:", error);
      setSnackbarMessage("Error fetching subject details.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSave = async () => {
    console.log("Subject Details Before Save:", subjectDetails);
    const invalidSubjects = subjectDetails.filter(
      (subject) => !subject.date || !subject.startTime || !subject.endTime
    );
    if (invalidSubjects.length > 0) {
      setDialogMessage("Please select a date and time for all subjects.");
      setDialogOpen(true);
      return;
    }
    const validSubjectDetails = subjectDetails.map((subject) => ({
      classId: selectedClass,
      sectionId: selectedSection,
      sessionId: session,
      subjectId: subject.subjectId,
      examCategoryId: selectedExamType,
      subExamId: selectedSubExam || 0,
      examDate: formatDate(subject.date),
      examStartTime: subject.startTime,
      examEndTime: subject.endTime,
      minMarks: subject.minMarks,
      maxMarks: subject.maxMarks,
      admitCardRemarks: "",
    }));
    if (validSubjectDetails.length === 0) {
      setSnackbarMessage("No subject details to save.");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }
    try {
      const Url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${Url}/AdmitCard/InsertAdmitCards`;
      const token = sessionStorage.getItem("token");
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
        },
        body: JSON.stringify(validSubjectDetails),
      });
      const data = await response.json();
      console.log("API Response:", data);
      if (response.ok) {
        setSelectedExamType("");
        setSelectedSubExam("");
        setSelectedClass("");
        setSelectedSection("");
        setSelectedSession("");
        setSubjectDetails([]);
        setSnackbarMessage("Admit cards saved successfully.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else {
        setSnackbarMessage(data.message || "Failed to save admit cards.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error("Error while saving:", error);
      setSnackbarMessage("Error while saving: " + error.message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleSessionChange = (event) => setSession(event.target.value);

  return (
    <Box p={3}>
<style>{styles}</style>
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: "bold" }}>
            Create New Admit Card{" "}
            <InfoIcon
              onClick={() => setShowUserGuide(true)}
              style={{ opacity: "0.6" }}
            />
          </Typography>
          <Dialog
            open={showUserGuide}
            onClose={() => setShowUserGuide(false)}
            maxWidth="md"
            fullWidth
            sx={{
              "& .MuiDialog-paper": {
                borderRadius: "12px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
                backgroundColor: "#f9fafb",
              },
            }}
          >
            <DialogTitle
              sx={{
                background: "linear-gradient(90deg, #1898c2 0%, #beab6c 100%)",
                color: "white",
                fontWeight: "bold",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                py: 2,
              }}
            >
              Admit Card User Guide
            </DialogTitle>
            <DialogContent sx={{ padding: "24px" }}>
              <Typography
                variant="h6"
                sx={{
                  color: "#1898c2",
                  fontWeight: "600",
                  mb: 2,
                  mt: 1,
                  textAlign: "center",
                }}
              >
                Quick Steps to Create New Admit Card
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyleType: "none",
                  padding: 0,
                  "& li": {
                    display: "flex",
                    alignItems: "flex-start",
                    mb: 1.5,
                    fontSize: "1.0rem",
                    color: "#333",
                  },
                  "& li::before": {
                    content: '"\\2022"',
                    color: "#1898c2",
                    fontWeight: "bold",
                    display: "inline-block",
                    width: "1.5em",
                    mr: 1,
                    fontSize: "1.2rem",
                  },
                  "& strong": {
                    margin: "0 0.2em",
                  },
                  "& em": {
                    margin: "0 0.2em",
                  },
                }}
              >
                <li>
                  Select an <strong>Exam Type</strong> from the dropdown menu.
                </li>
                <li>
                  If applicable, select a <strong>Sub Exam</strong> from the
                  dropdown menu.
                </li>
                <li>
                  Choose a <strong>Session</strong> from the dropdown menu.
                </li>
                <li>
                  Select a <strong>Class</strong> from the dropdown menu.
                </li>
                <li>
                  Select a <strong>Section</strong> from the dropdown menu.
                </li>
                <li>
                  Click the <strong>GET DETAILS</strong> button to retrieve subject
                  details based on the selected criteria.
                </li>
                <li>
                  For each subject, specify the <strong>Date</strong>,{" "}
                  <strong>Start Time</strong>, and <strong>End Time</strong>.
                </li>
                <li>
                  Click the <strong>Save</strong> button to save the admit card
                  details.
                </li>
                <li>
                  If you need to start over, click the <strong>Cancel</strong>{" "}
                  button to clear the form.
                </li>
                <li>
                  To navigate back to the Admit Card view, click the{" "}
                  <strong>Admit Card</strong> button.
                </li>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
              <StyledButton
                onClick={() => setShowUserGuide(false)}
                color="primary"
                variant="contained"
                sx={{
                  backgroundColor: "#1898c2",
                  "&:hover": { backgroundColor: "#156f9a" },
                  borderRadius: "8px",
                  px: 4,
                }}
              >
                Close
              </StyledButton>
            </DialogActions>
          </Dialog>
        </Toolbar>
      </AppBar>
      <div style={{ display: "flex", marginTop: 20, marginLeft: 80 }}>
        <ButtonGroup>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleNavigateToAdmitCard}
          >
            Admit Card
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleNavigateToCreateNewAdmit}
          >
            Create New Admit
          </Button>
        </ButtonGroup>
      </div>
      <Paper
        elevation={3}
        sx={{ padding: 4, margin: "auto", maxWidth: 1000, marginTop: 3 }}
      >
        <Grid
          container
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="exam-type-label">Exam Type</InputLabel>
              <Select
                labelId="exam-type-label"
                id="exam-type"
                value={selectedExamType}
                onChange={(e) => setSelectedExamType(e.target.value)}
                label="Exam Type"
              >
                {examTypes.map((type) => (
                  <MenuItem key={type.examCriteriaId} value={type.examCriteriaId}>
                    {type.examType}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth disabled={isSubExamDisabled}>
              <InputLabel id="sub-exam-label">Sub Exam</InputLabel>
              <Select
                labelId="sub-exam-label"
                id="sub-exam"
                value={selectedSubExam}
                onChange={(e) => setSelectedSubExam(e.target.value)}
                label="Sub Exam"
              >
                {subExams.map((subExam) => (
                  <MenuItem key={subExam.id} value={subExam.id}>
                    {subExam.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="session-label">Session</InputLabel>
              <Select
                labelId="session-label"
                id="session"
                value={session}
                onChange={handleSessionChange}
                label="Session"
              >
                {sessions.map((session) => (
                  <MenuItem key={session.id} value={session.id}>
                    {session.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box mt={4} display="flex" justifyContent="center" gap={2}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="class-label">Class</InputLabel>
            <Select
              labelId="class-label"
              id="class"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              label="Class"
            >
              {classNames.map((className) => (
                <MenuItem key={className.id} value={className.id}>
                  {className.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="section-label">Section</InputLabel>
            <Select
              labelId="section-label"
              id="section"
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              label="Section"
            >
              {sections.map((section) => (
                <MenuItem key={section.id} value={section.id}>
                  {section.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box mt={4} display="flex" justifyContent="center">
          <Button variant="contained" color="primary" onClick={handleGetDetails}>
            GET DETAILS
          </Button>
        </Box>
        <Box mt={4}>
          <TableContainer component={Paper} sx={{ width: "100%" }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Subject
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Date
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    Start Time
                  </TableCell>
                  <TableCell
                    style={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}
                  >
                    End Time
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjectDetails.length > 0 ? (
                  subjectDetails.map((subject, index) => (
                    <TableRow key={index}>
                      <TableCell>{subject.subjectName}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          type="date"
                          value={subject.date}
                          onChange={(e) => handleDateChange(index, e)}
                        />
                      </TableCell>
                     <TableCell>
  <TimePicker
    onChange={(value) => {
      const updatedSubjectDetails = [...subjectDetails];
      updatedSubjectDetails[index].startTime = value;
      setSubjectDetails(updatedSubjectDetails);
    }}
    value={subject.startTime}
    disableClock={true}
    format="h:mm a"
    className="custom-time-picker"
  />
</TableCell>
<TableCell>
  <TimePicker
    onChange={(value) => {
      const updatedSubjectDetails = [...subjectDetails];
      updatedSubjectDetails[index].endTime = value;
      setSubjectDetails(updatedSubjectDetails);
    }}
    value={subject.endTime}
    disableClock={true}
    format="h:mm a"
    className="custom-time-picker"
  />
</TableCell>

                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      No subject details found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        <Box mt={4} display="flex" justifyContent="center" gap={2}>
          <Button variant="contained" color="primary" onClick={handleSave}>
            Save
          </Button>
          <Button variant="outlined" color="secondary">
            Cancel
          </Button>
        </Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Dialog open={openDialog} onClose={handleDialogClose}>
          <DialogTitle>Missing Information</DialogTitle>
          <DialogContent>
            Please select all required details before proceeding.
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDialogClose} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Validation Error</DialogTitle>
          <DialogContent>{dialogMessage}</DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Box>
  );
};

export default AdmitPage;